import * as React from 'react';
import { Box, Grid } from '@mui/material';
import { TrashIcon, UploadIcon } from 'assets/icons';
import './index.scss';
import { extensionToType, handleContentDataDelete, uploadConfig } from 'utils/file-uploader';
import { UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import useFileUpload from 'base/core/hooks/use-file-upload';
import { IconLoadingButton } from '../../molecules/custom-button';
import ContentMediaView from '../content-media-view';
import ProgressBar from '../progress-bar';

interface FileUploaderProps {
  handleFile: (res: UploadResponse) => void,
  handleRemove: () => void,
  currentContentUrl: string,
  scenario: UploadScenarios
  currentMediaType: string,
  uploadBtnTxt?: string,
  imgMediaStyle?: any;
  videoMediaStyle?: any;
}

const FileUploader = ({
  handleFile, handleRemove, currentContentUrl, scenario, currentMediaType, uploadBtnTxt,
  imgMediaStyle, videoMediaStyle,
}: FileUploaderProps) => {
  const [uploading, setUploading] = React.useState(false);
  const [contentUrl, setContentUrl] = React.useState(currentContentUrl);
  const [mediaType, setMediaType] = React.useState(currentMediaType);
  const hiddenFileInput: any = React.useRef(null);
  const allowedExtension = (uploadConfig.get(scenario)?.allowedExtensions as string[]).join(',');
  const { progress, handleContentDataUpload } = useFileUpload();
  const [fileName, setFileName] = React.useState('');

  React.useEffect(() => {
    setContentUrl(currentContentUrl);
  }, [currentContentUrl]);

  // triggers the native input file button on click
  const handleClick = () => {
    hiddenFileInput.current.click();
  };

  // TODO: create a remove function if needed
  const handleRemoveWrapper = async () => {
    setUploading(true);
    await handleContentDataDelete(contentUrl, scenario);
    setContentUrl('');
    handleRemove();
    setUploading(false);
  };

  // handles file upload and send the image content data object to the parent component

  const handleChange = async (event: any) => {
    setUploading(true);
    if (event.target.files.length > 0) {
      const res = await handleContentDataUpload(
        event,
        currentContentUrl,
        scenario,
      );
      setTimeout(() => {
        setContentUrl(res.url);
        setFileName(res.fileData?.fileName || `${res.name}${res.extension}`);
        setMediaType(extensionToType.get(res.extension) as string);
        handleFile(res);
        setUploading(false);
      }, 3000);
    } else {
      setUploading(false);
    }
  };

  const mediaStyles = mediaType === 'image' ? imgMediaStyle : videoMediaStyle;

  return (
    <>
      <Grid container justifyContent="flex-start" alignItems="center" spacing={2}>
        <Grid item xs={12} sm="auto">
          <div style={mediaStyles} className="rounded overflow-hidden">
            <ContentMediaView url={contentUrl} mediaType={mediaType} fileName={fileName} />
          </div>
        </Grid>
        <Grid item xs={6} sm="auto">
          <IconLoadingButton
            startIcon={<UploadIcon sx={{ color: 'primary.main' }} />}
            loading={uploading}
            onClick={handleClick}
          >
            {uploadBtnTxt}
          </IconLoadingButton>
          <input
            type="file"
            accept={allowedExtension}
            ref={hiddenFileInput}
            onChange={handleChange}
            style={{ display: 'none' }}
          />
        </Grid>
        <Grid item xs={6} sm="auto">
          <IconLoadingButton
            startIcon={<TrashIcon sx={{ color: 'primary.main' }} />}
            onClick={handleRemoveWrapper}
          >
            Delete
          </IconLoadingButton>
        </Grid>
      </Grid>
      {progress > -1 && (
        <Box sx={{ mt: 2 }}>
          <ProgressBar progress={progress} />
        </Box>
      )}
    </>
  );
};

FileUploader.defaultProps = {
  uploadBtnTxt: 'Upload new',
  imgMediaStyle: { height: '6rem', width: '6rem' },
  videoMediaStyle: { height: '8rem', width: '100%' },
};

export default FileUploader;
